// Considered: StickyUnsavedBarMolecule — rejected: a page-level "pending edits" bar with fixed Save/Discard actions, not "a change already made".
// Considered: PrimeReact Toast — rejected: floats over content and auto-times out, which fails WCAG 2.2.1 once it holds an input. See spec Reuse Audit.
import React, { useEffect, useRef } from 'react';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './UndoStripMolecule.scss';

export type UndoStripVariant = 'default' | 'error';

export interface UndoStripMoleculeProps {
  /** What happened (default) or what failed (error). */
  message: string;
  variant?: UndoStripVariant;
  /** Slot between the message and the actions, e.g. an inline reason field. */
  extra?: React.ReactNode;
  undoLabel: string;
  dismissLabel: string;
  retryLabel: string;
  onUndo?: () => void;
  onDismiss: () => void;
  /** Error variant only. Omit when retrying cannot help (a forbidden error) so the strip offers Dismiss alone. */
  onRetry?: () => void;
  /** An Undo or Retry request is in flight. */
  busy?: boolean;
  /** Moves focus to the primary action (Undo, or Retry on error) whenever this value changes. */
  focusKey?: string | number;
  className?: string;
}

const UndoStripMolecule: React.FC<UndoStripMoleculeProps> = ({
  message,
  variant = 'default',
  extra,
  undoLabel,
  dismissLabel,
  retryLabel,
  onUndo,
  onDismiss,
  onRetry,
  busy = false,
  focusKey,
  className = '',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const isError = variant === 'error';

  useEffect(() => {
    if (focusKey === undefined) return;
    rootRef.current?.querySelector<HTMLElement>('[data-primary-action]')?.focus();
  }, [focusKey]);

  const classes = ['undo-strip', `undo-strip--${variant}`, className].filter(Boolean).join(' ');

  return (
    <div
      ref={rootRef}
      className={classes}
      role="status"
      aria-live="polite"
      data-variant={variant}
    >
      <span className="undo-strip__message">{message}</span>
      {extra ? <div className="undo-strip__extra">{extra}</div> : null}
      <div className="undo-strip__actions">
        {!isError && onUndo ? (
          <ButtonAtom
            variant="text"
            size="small"
            label={undoLabel}
            loading={busy}
            disabled={busy}
            onClick={onUndo}
            data-primary-action=""
          />
        ) : null}
        {isError && onRetry ? (
          <ButtonAtom
            variant="text"
            size="small"
            label={retryLabel}
            loading={busy}
            disabled={busy}
            onClick={onRetry}
            data-primary-action=""
          />
        ) : null}
        <ButtonAtom
          variant="text"
          size="small"
          label={dismissLabel}
          disabled={busy}
          onClick={onDismiss}
        />
      </div>
    </div>
  );
};

export default UndoStripMolecule;
